import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ApolloError } from '@apollo/client';
import {
    EntityType,
    FacetFilterInput,
    FacetMetadata,
    SearchAcrossEntitiesInput,
} from '../../../../../../types.generated';
import { DEGREE_FILTER_NAME, ENTITY_FILTER_NAME, UnionType } from '../../../../../search/utils/constants';
import { SearchCfg } from '../../../../../../conf';
import { EmbeddedListSearchResults } from './EmbeddedListSearchResults';
import EmbeddedListSearchHeader from './EmbeddedListSearchHeader';
import { useGetSearchResultsForMultipleQuery } from '../../../../../../graphql/search.generated';
import { FilterSet, GetSearchResultsParams, SearchResultsInterface } from './types';
import { EntityAndType } from '../../../types';
import { generateOrFilters } from '../../../../../search/utils/generateOrFilters';
import { mergeFilterSets } from '../../../../../search/utils/filterUtils';
import { useEntityContext } from '../../../EntityContext';
import { EntityActionProps } from './EntitySearchResults';
import { useUserContext } from '../../../../../context/useUserContext';
import analytics, { EventType } from '../../../../../analytics';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: hidden;

    @media (max-width: 992px) {
        height: auto;
    }
`;

// this extracts the response from useGetSearchResultsForMultipleQuery into a common interface other search endpoints can also produce
function useWrappedSearchResults(params: GetSearchResultsParams) {
    const { data, loading, error, refetch } = useGetSearchResultsForMultipleQuery(params);
    return {
        data: data?.searchAcrossEntities,
        loading,
        error,
        refetch: (refetchParams: GetSearchResultsParams['variables']) =>
            refetch(refetchParams).then((res) => res.data.searchAcrossEntities),
    };
}

// the addFixedQuery checks and generate the query as per params pass to embeddedListSearch
export const addFixedQuery = (baseQuery: string, fixedQuery: string, emptyQuery: string) => {
    let finalQuery = ``;
    if (baseQuery && fixedQuery) {
        finalQuery = baseQuery.includes(fixedQuery) ? `${baseQuery}` : `(*${baseQuery}*) AND (${fixedQuery})`;
    } else if (baseQuery) {
        finalQuery = `${baseQuery}`;
    } else if (fixedQuery) {
        finalQuery = `${fixedQuery}`;
    } else {
        return emptyQuery || '';
    }
    return finalQuery;
};

// Simply remove the fields that were marked as fixed from the facets that the server
// responds.
export const removeFixedFiltersFromFacets = (fixedFilters: FilterSet, facets: FacetMetadata[]) => {
    const fixedFields = fixedFilters.filters.map((filter) => filter.field);
    return facets.filter((facet) => !fixedFields.includes(facet.field));
};

type Props = {
    query: string;
    entityTypes?: EntityType[];
    page: number;
    unionType: UnionType;
    filters: FacetFilterInput[];
    onChangeQuery: (query) => void;
    onChangeFilters: (filters) => void;
    onChangePage: (page) => void;
    onChangeUnionType: (unionType: UnionType) => void;
    onTotalChanged?: (newTotal: number) => void;
    emptySearchQuery?: string | null;
    fixedFilters?: FilterSet;
    fixedQuery?: string | null;
    placeholderText?: string | null;
    defaultShowFilters?: boolean;
    defaultFilters?: Array<FacetFilterInput>;
    searchBarStyle?: any;
    searchBarInputStyle?: any;
    entityAction?: React.FC<EntityActionProps>;
    skipCache?: boolean;
    useGetSearchResults?: (params: GetSearchResultsParams) => {
        data: SearchResultsInterface | undefined | null;
        loading: boolean;
        error: ApolloError | undefined;
        refetch: (variables: GetSearchResultsParams['variables']) => Promise<SearchResultsInterface | undefined | null>;
    };
    shouldRefetch?: boolean;
    resetShouldRefetch?: () => void;
    applyView?: boolean;
    overrideSearchAPI?: boolean;
    overrideCheckDownloadType?: boolean;
};

export const EmbeddedListSearch = ({
    query,
    entityTypes,
    filters,
    page,
    unionType,
    onChangeQuery,
    onChangeFilters,
    onChangePage,
    onChangeUnionType,
    onTotalChanged,
    emptySearchQuery,
    fixedFilters,
    fixedQuery,
    placeholderText,
    defaultShowFilters,
    defaultFilters,
    searchBarStyle,
    searchBarInputStyle,
    entityAction,
    skipCache,
    useGetSearchResults = useWrappedSearchResults,
    shouldRefetch,
    resetShouldRefetch,
    overrideSearchAPI = false,
    overrideCheckDownloadType = false,
    applyView = false,
}: Props) => {
    const { shouldRefetchEmbeddedListSearch, setShouldRefetchEmbeddedListSearch } = useEntityContext();
    // Adjust query based on props
    const finalQuery: string = addFixedQuery(query as string, fixedQuery as string, emptySearchQuery as string);

    const baseFilters = {
        unionType,
        filters,
    };

    const finalFilters =
        (fixedFilters && mergeFilterSets(fixedFilters, baseFilters)) || generateOrFilters(unionType, filters);

    const entityFilters: Array<EntityType> = filters
        .filter((filter) => filter.field === ENTITY_FILTER_NAME)
        .flatMap((filter) => filter.values?.map((value) => value?.toUpperCase() as EntityType) || []);

    const [showFilters, setShowFilters] = useState(defaultShowFilters || false);
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<EntityAndType[]>([]);
    const [numResultsPerPage, setNumResultsPerPage] = useState(SearchCfg.RESULTS_PER_PAGE);

    const userContext = useUserContext();
    const selectedViewUrn = userContext.localState?.selectedViewUrn;

    let searchInput: SearchAcrossEntitiesInput = {
        types: entityTypes || [],
        query: finalQuery,
        start: (page - 1) * numResultsPerPage,
        count: numResultsPerPage,
        orFilters: finalFilters,
        viewUrn: applyView ? selectedViewUrn : undefined,
    };
    if (skipCache) {
        searchInput = { ...searchInput, searchFlags: { skipCache: true } };
    }

    const { data, loading, refetch } = useGetSearchResults({
        variables: {
            input: searchInput,
        },
        errorPolicy: 'all',
    });

    const callSearchOnVariables = (variables: GetSearchResultsParams['variables']) => {
        return refetch(variables);
    };

    useEffect(() => {
        if (shouldRefetch && resetShouldRefetch) {
            refetch({
                input: searchInput,
            });
            resetShouldRefetch();
        }
    });

    useEffect(() => {
        if (shouldRefetchEmbeddedListSearch) {
            refetch({
                input: searchInput,
            });
            setShouldRefetchEmbeddedListSearch?.(false);
        }
    });

    useEffect(() => {
        if (data?.total !== undefined && onTotalChanged) {
            onTotalChanged(data?.total);
        }
    }, [data?.total, onTotalChanged]);

    const searchResultEntities =
        data?.searchResults?.map((result) => ({ urn: result.entity.urn, type: result.entity.type })) || [];
    const searchResultUrns = searchResultEntities.map((entity) => entity.urn);
    const selectedEntityUrns = selectedEntities.map((entity) => entity.urn);

    // Calculate selectable entities (excluding read-only datasets)
    const selectableEntities = searchResultEntities.filter((entity) => {
        const searchResult = data?.searchResults?.find((result) => result.entity.urn === entity.urn);
        const isReadOnly =
            entity.type === EntityType.Dataset && (searchResult?.entity as any)?.readOnly?.readOnly === true;
        return !isReadOnly;
    });
    const selectableUrns = selectableEntities.map((entity) => entity.urn);

    // Calculate isSelectAll: all selectable entities are selected
    const isSelectAll = selectableUrns.length > 0 && selectableUrns.every((urn) => selectedEntityUrns.includes(urn));

    // Calculate indeterminate state for select all checkbox
    const isIndeterminate = () => {
        const selectedCount = selectableUrns.filter((urn) => selectedEntityUrns.includes(urn)).length;
        return selectedCount > 0 && selectedCount < selectableUrns.length;
    };

    const onToggleFilters = () => {
        setShowFilters(!showFilters);
    };

    /**
     * Invoked when the "select all" checkbox is clicked.
     *
     * This method either adds the entire current page of search results to
     * the list of selected entities, or removes the current page from the set of selected entities.
     */
    const onChangeSelectAll = (selected: boolean) => {
        if (selected) {
            // Add current page of urns to the master selected entity list, excluding read-only datasets
            const entitiesToAdd = searchResultEntities.filter((entity) => {
                const isAlreadySelected =
                    selectedEntities.findIndex(
                        (element) => element.urn === entity.urn && element.type === entity.type,
                    ) >= 0;
                const searchResult = data?.searchResults?.find((result) => result.entity.urn === entity.urn);
                const isReadOnly =
                    entity.type === EntityType.Dataset && (searchResult?.entity as any)?.readOnly?.readOnly === true;
                return !isAlreadySelected && !isReadOnly;
            });
            setSelectedEntities(Array.from(new Set(selectedEntities.concat(entitiesToAdd))));
        } else {
            // Filter out the current page of entity urns from the list
            setSelectedEntities(selectedEntities.filter((entity) => searchResultUrns.indexOf(entity.urn) === -1));
        }
    };

    useEffect(() => {
        if (!isSelectMode) {
            setSelectedEntities([]);
        }
    }, [isSelectMode]);

    useEffect(() => {
        if (defaultFilters && filters.length === 0) {
            onChangeFilters(defaultFilters);
        }
        // only want to run once on page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Compute the final Facet fields that we show in the left hand search Filters (aggregation).
     *
     * Do this by filtering out any fields that are included in the fixed filters.
     */
    const finalFacets =
        (fixedFilters && removeFixedFiltersFromFacets(fixedFilters, data?.facets || [])) || data?.facets;

    // used for logging impact anlaysis events
    const degreeFilter = filters.find((filter) => filter.field === DEGREE_FILTER_NAME);

    // we already have some lineage logging through Tab events, but this adds additional context, particularly degree
    if (!loading && (degreeFilter?.values?.length || 0) > 0) {
        analytics.event({
            type: EventType.SearchAcrossLineageResultsViewEvent,
            query,
            page,
            total: data?.total || 0,
            maxDegree: degreeFilter?.values?.sort()?.reverse()[0] || '1',
        });
    }

    return (
        <Container>
            <EmbeddedListSearchHeader
                onSearch={(q) => onChangeQuery(addFixedQuery(q, fixedQuery as string, emptySearchQuery as string))}
                placeholderText={placeholderText}
                entityFilters={entityFilters}
                onToggleFilters={onToggleFilters}
                callSearchOnVariables={overrideSearchAPI ? callSearchOnVariables : undefined}
                filters={finalFilters}
                query={finalQuery}
                totalResults={data?.total || 0}
                isSelectMode={isSelectMode}
                isSelectAll={isSelectAll}
                isIndeterminate={isIndeterminate()}
                setIsSelectMode={setIsSelectMode}
                selectedEntities={selectedEntities}
                onChangeSelectAll={onChangeSelectAll}
                refetch={() => refetch({ input: searchInput })}
                searchBarStyle={searchBarStyle}
                searchBarInputStyle={searchBarInputStyle}
                overrideCheckDownloadType={overrideCheckDownloadType}
            />
            <EmbeddedListSearchResults
                unionType={unionType}
                loading={loading}
                searchResponse={data}
                filters={finalFacets}
                allFilters={data?.facets}
                selectedFilters={filters}
                onChangeFilters={onChangeFilters}
                onChangePage={onChangePage}
                onChangeUnionType={onChangeUnionType}
                page={page}
                showFilters={showFilters}
                numResultsPerPage={numResultsPerPage}
                setNumResultsPerPage={setNumResultsPerPage}
                isSelectMode={isSelectMode}
                selectedEntities={selectedEntities}
                setSelectedEntities={setSelectedEntities}
                entityAction={entityAction}
                applyView={applyView}
            />
        </Container>
    );
};
